import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentBet,
  selectIsAuctionEnabled,
  selectIsAuctionSelected,
  selectIsAutobrokerEnabled,
  selectNextBets,
  selectPreviousBets,
} from '../../../../../../../selectors/auction';
import { ECheckServiceStatus } from '../../../../../../../types/reducers/checkoutInfo';
import { selectCheckServiceStatus } from '../../../../../../../selectors/checkoutInfo';
import { isBetInputVisible, saveBetToStore } from './utils';
import { selectIsTopServiceUnavailable } from '../../../../../../../selectors/publishTerms';

interface IUseAuctionStateResult {
  currentBet: number;
  hasPreviousBet: boolean;
  hasNextBet: boolean;
  loading: boolean;
  showBetInput: boolean;

  updateBet(bet: number): void;

  decreaseBet(): void;

  increaseBet(): void;
}

export function useAuctionState(): IUseAuctionStateResult {
  const dispatch = useDispatch();
  const defaultBet = useSelector(selectCurrentBet);
  const previousBets = useSelector(selectPreviousBets);
  const nextBets = useSelector(selectNextBets);
  const status = useSelector(selectCheckServiceStatus);
  const isAuctionSelected = useSelector(selectIsAuctionSelected);
  const isAutobrokerEnabled = useSelector(selectIsAutobrokerEnabled);
  const isTopServiceUnavailable = useSelector(selectIsTopServiceUnavailable);
  const isAuctionEnabled = useSelector(selectIsAuctionEnabled);

  const bets = React.useMemo(
    () => [...previousBets, defaultBet, ...nextBets],
    // Не меняем массив ставок, если поменялся defaultBet
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previousBets, nextBets],
  );

  const defaultBetIndex = previousBets.length;

  const [bet, setBet] = React.useState(defaultBet);
  const [betIndex, setBetIndex] = React.useState(defaultBetIndex);

  const previousBet = bets[betIndex - 1] ?? null;
  const nextBet = bets[betIndex + 1] ?? null;

  const handleSetBet = useCallback(
    (value: number) => {
      if (value === bet) {
        return;
      }
      setBet(value);
      saveBetToStore(dispatch, value, 'input');
    },
    [bet, dispatch],
  );

  const decreaseBet = React.useCallback(() => {
    saveBetToStore(dispatch, previousBet, 'minus');
    setBet(previousBet);
    setBetIndex(betIndex - 1);
  }, [dispatch, previousBet, betIndex]);

  const increaseBet = React.useCallback(() => {
    saveBetToStore(dispatch, nextBet, 'plus');
    setBet(nextBet);
    setBetIndex(betIndex + 1);
  }, [dispatch, nextBet, betIndex]);

  React.useEffect(
    () => setBetIndex(defaultBetIndex),
    // Если предыдущие/следующие ставки поменялись, значит выполнился запрос check-service и подгрузились обновленные ставки
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previousBets, nextBets],
  );

  React.useEffect(
    () => setBet(defaultBet),
    // Если дефолтная ставка поменялась, значит бэк округлил ставку, синхронизируем ее
    [defaultBet],
  );

  const loading = status === ECheckServiceStatus.pending && isAuctionSelected;
  const showBetInput = isBetInputVisible(isAutobrokerEnabled, isTopServiceUnavailable, isAuctionEnabled);

  return {
    currentBet: bet,
    hasPreviousBet: previousBet !== null,
    hasNextBet: nextBet !== null,
    updateBet: handleSetBet,
    decreaseBet,
    increaseBet,
    loading,
    showBetInput,
  };
}
